import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Button, Space, Table, Empty, Popconfirm, message, Input, Card, Statistic, Tag, Row, Col, Badge, Select, Tabs } from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined, 
  SearchOutlined,
  TeamOutlined,
  ApartmentOutlined,
  ClusterOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { departmentsApi } from '../../../utils/api';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useListReturnState } from '../../../hooks/useListReturnState';

const getDepartmentMembers = (department) => {
  return Array.isArray(department?.team_members) ? department.team_members : [];
};

// تبدیل لیست تخت دپارتمان‌ها به ساختار درختی برای نمایش سازمانی
const buildDepartmentTree = (list) => {
  if (!list?.length) return [];
  const byId = {};
  list.forEach((d) => {
    byId[d.id] = { ...d, children: [] };
  });
  const roots = [];
  list.forEach((d) => {
    const node = byId[d.id];
    const parentId = d.parent_id ?? d.parent?.id;
    if (parentId == null || !byId[parentId]) {
      roots.push(node);
    } else {
      byId[parentId].children.push(node);
    }
  });
  roots.forEach((r) => {
    r.children.sort((a, b) => (a.team_name || '').localeCompare(b.team_name || ''));
  });
  roots.sort((a, b) => (a.team_name || '').localeCompare(b.team_name || ''));
  return roots;
};

// کامپوننت نمایش یک گره در نمودار سازمانی (هرمی)
const OrgChartNode = ({ node, onNavigate, level = 0 }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isRoot = level === 0;
  const members = getDepartmentMembers(node);
  const visibleMembers = members.slice(0, 4);
  const remainingMembers = Math.max(members.length - visibleMembers.length, 0);

  return (
    <div className="org-chart-node-wrapper" style={{ display: 'inline-block', verticalAlign: 'top' }}>
      <div className="org-chart-node-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => onNavigate(node.id)}
          onKeyDown={(e) => e.key === 'Enter' && onNavigate(node.id)}
          className={`org-chart-card ${isRoot ? 'org-chart-card--root' : 'org-chart-card--child'}`}
          style={{
            fontFamily: 'CustomFont',
            minWidth: isRoot ? 220 : 190,
            maxWidth: 260,
            padding: isRoot ? '14px 18px' : '12px 16px',
            borderRadius: 12,
            background: '#fff',
            color: '#1e293b',
            boxShadow: '0 2px 10px rgba(15, 23, 42, 0.08)',
            border: isRoot ? '2px solid #0D887E' : '1px solid #e5e7eb',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            textAlign: 'right',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <ApartmentOutlined style={{ fontSize: isRoot ? 20 : 16, color: '#0D887E' }} />
            <span style={{ fontWeight: isRoot ? 700 : 600, fontSize: isRoot ? 15 : 14 }}>
              {node.team_name}
            </span>
          </div>
          {node.team_members_count != null && (
            <div style={{ fontSize: 11, marginTop: 4, color: '#64748b', textAlign: 'center' }}>
              {node.team_members_count} نفر
            </div>
          )}
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {visibleMembers.length > 0 ? (
              visibleMembers.map((member) => (
                <div
                  key={member.id || member.user_id}
                  className="org-chart-member"
                  style={{
                    padding: '5px 8px',
                    borderRadius: 8,
                    background: '#f8fafc',
                    color: '#475569',
                    fontSize: 12,
                    lineHeight: 1.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {member.user?.name || member.name || 'بدون نام'}
                </div>
              ))
            ) : (
              <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center' }}>بدون کارمند</div>
            )}
            {remainingMembers > 0 && (
              <div style={{ color: '#0D887E', fontSize: 12, textAlign: 'center', fontWeight: 600 }}>
                +{remainingMembers} نفر دیگر
              </div>
            )}
          </div>
        </div>
        {hasChildren && (
          <>
            <div
              className="org-chart-vline"
              style={{
                width: 2,
                minHeight: 24,
                background: 'linear-gradient(to bottom, #94a3b8, #cbd5e1)',
                borderRadius: 1,
              }}
            />
            <div
              className="org-chart-children"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 24,
                position: 'relative',
              }}
            >
              {node.children.map((child) => (
                <OrgChartNode
                  key={child.id}
                  node={child}
                  onNavigate={onNavigate}
                  level={level + 1}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Departments = () => {
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = useResourcePermissions('employees');
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [parentFilter, setParentFilter] = useState(null);
  const [parentOptions, setParentOptions] = useState([]);
  const [activeTab, setActiveTab] = useState('list');
  const searchTimeoutRef = useRef(null);

  const captureCurrentListState = useCallback(
    () => ({
      searchText,
      parentFilter,
      activeTab,
    }),
    [searchText, parentFilter, activeTab],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.searchText != null) setSearchText(restored.searchText);
    if (restored.parentFilter !== undefined) setParentFilter(restored.parentFilter);
    if (restored.activeTab) setActiveTab(restored.activeTab);
  });

  useEffect(() => {
    fetchDepartments();
    loadParentOptions();
  }, []);

  const loadParentOptions = async () => {
    try {
      const res = await departmentsApi.getAll();
      setParentOptions(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchDepartments = async (query = '', selectedId = null) => {
    setLoading(true);
    try {
      const filters = {};
      
      // ✅ اگر ID انتخاب شده، فقط آن را بگیر
      if (selectedId) {
        filters.id = selectedId;
      }
      
      // ✅ اگر جستجو هست، بر اساس نام فیلتر کن
      if (query) {
        filters.team_name = query;
      }
      
      console.log('🔍 Fetching with filters:', filters);
      const response = await departmentsApi.getAll(filters);
      setDepartments(response.data || []);
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      message.error('خطا در دریافت دپارتمان‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await departmentsApi.delete(id);
      message.success('دپارتمان با موفقیت حذف شد');
      fetchDepartments(searchText, parentFilter);
    } catch (error) {
      console.error('Error deleting department:', error);
      message.error('خطا در حذف دپارتمان');
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchDepartments(value, parentFilter);
    }, 400);
  };

  const handleParentFilter = (val) => {
    setParentFilter(val || null);
    fetchDepartments(searchText, val || null);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // تعداد نتایج فیلتر شده
  const filteredDepartments = departments;

  // آمار
  const totalDepartments = departments.length;
  const departmentsWithParent = departments.filter(dept => dept.parent).length;
  const rootDepartments = totalDepartments - departmentsWithParent;

  const columns = [
    {
      title: 'نام دپارتمان',
      dataIndex: 'team_name',
      key: 'team_name',
      sorter: (a, b) => a.team_name.localeCompare(b.team_name),
      render: (text, record) => (
        <div className="flex items-center gap-2">
          <ApartmentOutlined className="text-blue-500" />
          <span 
            className="font-medium text-gray-800 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors"
            onClick={() => navigateToDetail(`/hr/departments/${record.id}`)}
            style={{ fontFamily: 'CustomFont' }}
          >
            {text}
          </span>
        </div>
      ),
    },
    {
      title: 'تعداد اعضا',
      dataIndex: 'team_members_count',
      key: 'team_members_count',
      width: 120,
      align: 'center',
      sorter: (a, b) => (a.team_members_count || 0) - (b.team_members_count || 0),
      render: (count) => (
        <Tag color={count > 0 ? 'blue' : 'default'} style={{ fontFamily: 'CustomFont' }}>
          {count || 0} نفر
        </Tag>
      ),
    },
    {
      title: 'دپارتمان والد',
      dataIndex: 'parent',
      key: 'parent',
      render: (parent) => (
        parent ? (
          <Tag 
            color="blue" 
            icon={<TeamOutlined />}
            style={{ 
              fontFamily: 'CustomFont', 
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '2px 8px',
              lineHeight: 1.6 
            }}
          >
            {parent.team_name}
          </Tag>
        ) : (
          <Badge status="default" text={<span style={{ fontFamily: 'CustomFont', color: '#999' }}>بدون والد</span>} />
        )
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigateToDetail(`/hr/departments/${record.id}`)}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            style={{ fontFamily: 'CustomFont' }}
            title="مشاهده"
          />
          {canEdit ? (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigateToDetail(`/hr/departments/${record.id}/edit`)}
              className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
              style={{ fontFamily: 'CustomFont' }}
              title="ویرایش"
            />
          ) : null}
          {canDelete ? (
            <Popconfirm
              title="حذف دپارتمان"
              description="آیا از حذف این دپارتمان اطمینان دارید؟"
              onConfirm={() => handleDelete(record.id)}
              okText="بله، حذف کن"
              cancelText="لغو"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                className="hover:bg-red-50 dark:hover:bg-red-900/20"
                style={{ fontFamily: 'CustomFont' }}
                title="حذف"
              />
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        {/* Main Container Card */}
        <Card 
          className="shadow-xl border-0"
          bodyStyle={{ padding: 0 }}
        >
          {/* Header Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                  <ApartmentOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
                </div>
                <div>
                  <h1 
                    className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    مدیریت دپارتمان‌ها
                  </h1>
                </div>
              </div>
              {canAdd ? (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/hr/departments/add')}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 border-0 shadow-md hover:shadow-lg transition-all"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  افزودن دپارتمان جدید
                </Button>
              ) : null}
            </div>
          </div>

          {/* Content Section */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {/* Statistics Cards */}
            <Row gutter={[16, 16]} className="mb-6">
              <Col xs={24} sm={8}>
                <Card className="shadow-md hover:shadow-lg transition-shadow border-0 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
                  <Statistic
                    title={<span style={{ fontFamily: 'CustomFont', color: '#666' }}>کل دپارتمان‌ها</span>}
                    value={totalDepartments}
                    prefix={<TeamOutlined className="text-blue-500" />}
                    valueStyle={{ color: '#1890ff', fontFamily: 'CustomFont' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card className="shadow-md hover:shadow-lg transition-shadow border-0 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20">
                  <Statistic
                    title={<span style={{ fontFamily: 'CustomFont', color: '#666' }}>دپارتمان‌های اصلی</span>}
                    value={rootDepartments}
                    prefix={<ApartmentOutlined className="text-green-500" />}
                    valueStyle={{ color: '#52c41a', fontFamily: 'CustomFont' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card className="shadow-md hover:shadow-lg transition-shadow border-0 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20">
                  <Statistic
                    title={<span style={{ fontFamily: 'CustomFont', color: '#666' }}>زیرمجموعه‌ها</span>}
                    value={departmentsWithParent}
                    prefix={<ClusterOutlined className="text-purple-500" />}
                    valueStyle={{ color: '#722ed1', fontFamily: 'CustomFont' }}
                  />
                </Card>
              </Col>
            </Row>

            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              size="middle"
              className="department-tabs"
              style={{ fontFamily: 'CustomFont' }}
              items={[
                {
                  key: 'list',
                  label: (
                    <span>
                      <UnorderedListOutlined style={{ marginLeft: 6 }} />
                      لیست دپارتمان‌ها
                    </span>
                  ),
                  children: (
                    <>
                      {/* Search and Filter Bar */}
                      <div className="mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" style={{ maxWidth: '800px' }}>
                <Input
                  placeholder="جستجو در نام دپارتمان..."
                  allowClear
                  onChange={handleSearchChange}
                  value={searchText}
                  style={{ fontFamily: 'CustomFont' }}
                  size="middle"
                  prefix={<SearchOutlined className="text-gray-400" />}
                  className="rounded-lg"
                />
                <Select
                  allowClear
                  showSearch
                  placeholder="انتخاب دپارتمان خاص"
                  value={parentFilter || undefined}
                  onChange={handleParentFilter}
                  filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  {parentOptions.map((dept) => (
                    <Select.Option key={dept.id} value={dept.id}>
                      {dept.team_name}
                    </Select.Option>
                  ))}
                </Select>
                        </div>
                      </div>

                      {/* Table */}
                      <div>
              {loading ? (
                <TableSkeleton rows={5} columns={3} />
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <TeamOutlined className="text-blue-500 text-lg" />
                    <span style={{ fontFamily: 'CustomFont', fontSize: '16px', fontWeight: 600 }}>
                      لیست دپارتمان‌ها
                    </span>
                    <Badge 
                      count={filteredDepartments.length} 
                      showZero 
                      className="mr-2"
                      style={{ backgroundColor: '#1890ff' }}
                    />
                  </div>
                  <Table
                    columns={columns}
                    dataSource={filteredDepartments}
                    rowKey="id"
                    loading={false}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => (
                    <span style={{ fontFamily: 'CustomFont' }}>
                      نمایش <strong>{range[0]}</strong> تا <strong>{range[1]}</strong> از <strong>{total}</strong> دپارتمان
                    </span>
                  ),
                  locale: {
                    items_per_page: 'ردیف در صفحه',
                  },
                }}
                locale={{
                  emptyText: (
                    <Empty
                      description={
                        <span style={{ fontFamily: 'CustomFont', color: '#999' }}>
                          {searchText || parentFilter ? 'نتیجه‌ای یافت نشد' : 'هنوز دپارتمانی ثبت نشده است'}
                        </span>
                      }
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
                }}
                    className="custom-table"
                    style={{ fontFamily: 'CustomFont' }}
                    rowClassName="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  />
                </>
              )}
                      </div>
                    </>
                  ),
                },
                {
                  key: 'chart',
                  label: (
                    <span>
                      <ApartmentOutlined style={{ marginLeft: 6 }} />
                      نمایش سازمانی
                    </span>
                  ),
                  children: (
                    <div className="org-chart-container" style={{ overflowX: 'auto', padding: '24px 0', minHeight: 280 }}>
                      {loading ? (
                        <TableSkeleton rows={3} columns={2} />
                      ) : buildDepartmentTree(departments).length === 0 ? (
                        <Empty
                          description={<span style={{ fontFamily: 'CustomFont' }}>دپارتمانی برای نمایش وجود ندارد</span>}
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 32 }}>
                          {buildDepartmentTree(departments).map((root) => (
                            <OrgChartNode
                              key={root.id}
                              node={root}
                              onNavigate={(id) => navigateToDetail(`/hr/departments/${id}`)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </Card>
      </div>

      <style jsx>{`
        :global(.custom-table .ant-table-thead > tr > th) {
          background: linear-gradient(to bottom, #fafafa, #f0f0f0) !important;
          font-weight: 600 !important;
          border-bottom: 2px solid #e8e8e8 !important;
        }
        :global(.dark .custom-table .ant-table-thead > tr > th) {
          background: linear-gradient(to bottom, #1f1f1f, #262626) !important;
          border-bottom: 2px solid #434343 !important;
        }
        :global(.custom-table .ant-table-tbody > tr:hover > td) {
          background: #f5f5f5 !important;
        }
        :global(.dark .custom-table .ant-table-tbody > tr:hover > td) {
          background: #262626 !important;
        }
        :global(.org-chart-card:hover) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
        }
        :global(.dark .org-chart-container .org-chart-card) {
          background: #1f2937 !important;
          color: #e5e7eb !important;
          border-color: #374151 !important;
        }
        :global(.dark .org-chart-container .org-chart-card--root) {
          border-color: #0D887E !important;
        }
        :global(.dark .org-chart-container .org-chart-member) {
          background: #111827 !important;
          color: #d1d5db !important;
        }
      `}</style>
    </MainLayout>
  );
};

export default Departments;
